import React, { useState } from "react";
import PropTypes from "prop-types";
import "./TextArea.scss";

/**
 * TextArea component for rendering a multi-line text input field.
 * Includes options for setting max length, character count display, 
 * and other custom configurations.
 *
 * @component
 * 
 * @param {Object} props - The properties for TextArea component.
 * @param {string} [props.name=""] - The name of the textarea field.
 * @param {string} [props.placeholder=`Please Enter ${name}`] - Placeholder text for the textarea.
 * @param {string} [props.value=""] - Current value of the textarea.
 * @param {function} props.onChange - Callback function to handle input change events.
 * @param {number} [props.textareaRows=5] - Number of rows for the textarea.
 * @param {number} props.textareaCols - Number of columns for the textarea.
 * @param {function} props.onBlur - Callback function to handle blur events.
 * @param {boolean} [props.isDisabled=false] - Flag to disable the textarea.
 * @param {boolean} [props.isReadOnly=false] - Flag to make the textarea read-only.
 * @param {boolean} [props.showCharacterLength=false] - Flag to show the character count.
 * @param {number} props.maxLength - Maximum length of the input.
 * @param {Object} rest - Additional props passed to the component.
 * 
 * @example
 * <TextArea
 *   name="description"
 *   placeholder="Enter your description here"
 *   value={value}
 *   onChange={handleInputChange}
 *   textareaRows={5}
 *   textareaCols={30}
 *   maxLength={200}
 *   showCharacterLength={true}
 * />
 */
const TextArea = ({
  name = "",
  keyId,
  placeholder = `Please Enter ${name}`,
  value = "",
  onBlur,
  onChange,
  textareaCols,
  textareaRows = 5,
  showCharacterLength = false,
  isdisable = false,
  isreadOnly = false,
  maxLength,
  ...rest
}) => {
  const [currentLength, setCurrentLength] = useState(value.length);

  const handleChange = (e) => {
    const inputValue = e.target.value;
    if (maxLength && inputValue.length > maxLength) return;
    setCurrentLength(inputValue.length);
    onChange?.(inputValue); 
  };

  return (
    <div className="text-area">
      <textarea
        key={keyId}
        className="text-area-input"
        placeholder={placeholder}
        value={value}
        name={name}
        onChange={handleChange}
        rows={textareaRows}
        cols={textareaCols}
        onBlur={onBlur}
        disabled={isdisable}
        readOnly={isreadOnly}
        {...rest}
      />
      {showCharacterLength && (
        <div className="text-area-length-info">
          <span>Current Length: {currentLength}</span>
          {!!maxLength && <span> / Max Length: {maxLength}</span>}
        </div>
      )}
    </div>
  );
};

// Define PropTypes for the component
TextArea.propTypes = {
  name: PropTypes.string,
  keyId: PropTypes.string,
  placeholder: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func,
  textareaRows: PropTypes.number,
  textareaCols: PropTypes.number,
  onBlur: PropTypes.func,
  isdisable: PropTypes.bool,
  isreadOnly: PropTypes.bool,
  maxLength: PropTypes.number,
  showCharacterLength: PropTypes.bool, // Added prop type
};

export default TextArea;
